import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';

export interface TakeoverProps {
  open: boolean;
  onDismiss: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  backLabel?: string;
  dismissible?: boolean;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Takeover({
  open,
  onDismiss,
  title,
  children,
  footer,
  backLabel = 'Back',
  dismissible = true,
}: TakeoverProps) {
  const isMobile = useIsMobile();
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const [entered, setEntered] = useState(false);

  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );



  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
    } else {
      if (triggerRef.current && 'focus' in triggerRef.current) {
        (triggerRef.current as HTMLElement).focus();
      }
      triggerRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (open && isMobile) {
      if (panelRef.current) {
        panelRef.current.style.transform = 'translateX(100%)';
        panelRef.current.style.transition = 'none';
      }
      const raf = requestAnimationFrame(() => {
        if (panelRef.current) {
          const duration = prefersReducedMotion.current ? '0ms' : '320ms';
          panelRef.current.style.transition = `transform ${duration} cubic-bezier(0.32, 0.72, 0, 1)`;
          panelRef.current.style.transform = 'translateX(0)';
        }
        setEntered(true);
      });
      return () => {
        cancelAnimationFrame(raf);
        setEntered(false);
      };
    }
    setEntered(false);
  }, [open, isMobile]);

  const animateOut = useCallback(
    (cb: () => void) => {
      if (!panelRef.current || prefersReducedMotion.current || !isMobile) {
        cb();
        return;
      }
      const duration = 240;
      const el = panelRef.current;
      el.style.transition = `transform ${duration}ms ease-in`;
      el.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (el.isConnected) cb();
      }, duration);
    },
    [isMobile]
  );

  const tryDismiss = useCallback(() => {
    if (!dismissible) return;
    animateOut(onDismiss);
  }, [dismissible, animateOut, onDismiss]);

  const handleFocusTrap = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const focusable = Array.from(
      panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)
    ).filter((el) => el.offsetParent !== null);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        handleFocusTrap(e);
        return;
      }
      if (e.key === 'Escape') tryDismiss();
    },
    [handleFocusTrap, tryDismiss]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      const focusable = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
      focusable?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, [open]);

  if (!open) return null;

  if (!isMobile) {
    return (
      <>
        <div
          className="fixed inset-0 bg-black/50 z-[300]"
          onClick={dismissible ? tryDismiss : undefined}
          aria-hidden="true"
          style={{
            opacity: 1,
            transition: prefersReducedMotion.current ? 'none' : 'opacity 150ms',
          }}
        />
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className="fixed inset-0 z-[300] flex items-center justify-center pointer-events-none"
        >
          <div
            className="bg-bg-primary w-full max-w-[600px] max-h-[90vh] rounded-2xl shadow-2xl flex flex-col pointer-events-auto"
          >
            <div className="flex items-center justify-between px-4 pt-4 pb-0 flex-shrink-0">
              {dismissible ? (
                <button
                  type="button"
                  onClick={tryDismiss}
                  className="min-w-[44px] min-h-[44px] flex items-center justify-center gap-1.5 rounded-lg text-text-secondary hover:text-text-primary transition-colors text-sm font-medium -ml-2"
                  aria-label={backLabel}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                  {backLabel}
                </button>
              ) : (
                <div />
              )}
              <h2 className="text-base font-semibold text-text-primary absolute left-1/2 -translate-x-1/2">
                {title}
              </h2>
              <div className="w-16" />
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0">{children}</div>

            {footer && (
              <div className="px-4 pt-3 pb-4 border-t border-border-light flex-shrink-0">
                {footer}
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-[300]"
        onClick={dismissible ? tryDismiss : undefined}
        aria-hidden="true"
        style={{
          opacity: entered ? 1 : 0,
          transition: prefersReducedMotion.current ? 'none' : 'opacity 150ms',
        }}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="fixed inset-0 z-[300] bg-bg-primary flex flex-col"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          transform: 'translateX(100%)',
        }}
      >
        <div className="flex items-center justify-between px-4 flex-shrink-0 h-14 relative">
          {dismissible ? (
            <button
              type="button"
              onClick={tryDismiss}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center gap-1.5 rounded-lg text-text-secondary hover:text-text-primary transition-colors text-sm font-medium -ml-2"
              aria-label={backLabel}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
              {backLabel}
            </button>
          ) : (
            <div className="w-16" />
          )}
          <h2 className="text-base font-semibold text-text-primary absolute left-1/2 -translate-x-1/2">
            {title}
          </h2>
          <div className="w-16" />
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0">{children}</div>

        {footer && (
          <div
            className="px-4 pt-3 border-t border-border-light flex-shrink-0"
            style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}
          >
            {footer}
          </div>
        )}
      </div>
    </>
  );
}
